import React, { lazy, Suspense, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Redirect, Route, Switch } from 'react-router-dom'
import { Dimmer, Loader } from 'semantic-ui-react'
import { IRoute } from '../types/route'
import { IUserDetail } from '../types/user'
import { checkRole } from '../utils/checkRole'

const ProjectListContainer = lazy(() => import('./project/containers/list/ProjectListContainer'))
const ProjectActivitiesContainer = lazy(() => import('./project/containers/activities/ProjectActivitiesContainer'))
const ProjectDashboardContainer = lazy(() => import('./project/containers/dashboard/ProjectDashboardContainer'))
const ProjectInsightsContainer = lazy(() => import('./project/containers/insights/ProjectInsightsContainer'))
const ProjectBudgetContainer = lazy(() => import('./project/containers/budget/BudgetListContainer'))
const ProjectMilestonesContainer = lazy(() => import('./project/containers/milestones/MilestonesContainer'))
const ProjectSettingsContainer = lazy(() => import('./project/containers/project-settings/ProjectSettingsContainer'))
const UserContainer = lazy(() => import('./settings/containers/UserContainer'))
const TimeTrackingContainer = lazy(() => import('./time-tracking/containers/TimeTrackingContainer'))
const TimeTrackingInvolvedTasksContainer = lazy(
  () => import('./time-tracking/containers/TimeTrackingInvolvedTasksContainer')
)
const GeneralContainer = lazy(() => import('./settings/containers/GeneralContainer'))
const RatesContainer = lazy(() => import('./settings/containers/RatesContainer'))
const ProjectAccountingContainer = lazy(() => import('./project/containers/accounting/AccountingContainer'))
const ProjectOfferContainer = lazy(
  () => import('./project/containers/budget/service-provider/ServiceProviderProjectOfferContainer')
)
const PaymentListContainer = lazy(() => import('./project/containers/accounting/PaymentListContainer'))
const InvoiceListContainer = lazy(() => import('./project/containers/accounting/InvoiceListContainer'))
const AccountingContainer = lazy(() => import('./accounting/containers/AccountingContainer'))
const UserProfileContainer = lazy(() => import('./settings/containers/UserProfileContainer'))
const GlobalDashboardContainer = lazy(() => import('./global-dashboard/containers/GlobalDashboardContainer'))
const UserDashboardContainer = lazy(() => import('./user-dashboard/containers/UserDashboardContainer'))
const GlobalReportsContainer = lazy(() => import('./reports/containers/ReportsContainer'))
const GlobalReportsTasksByProjectContainer = lazy(
  () => import('./reports/containers/projects-tab/TasksByProjectContainer')
)
const GlobalReportsProjectListContainer = lazy(
  () => import('./reports/containers/projects-tab/ProjectListReportContainer')
)
const GlobalReportsFTEByProjectContainer = lazy(() => import('./reports/containers/projects-tab/FTEByProjectContainer'))
const GlobalReportsExpenditurePhasingContainer = lazy(
  () => import('./reports/containers/projects-tab/ExpenditurePhasingContainer')
)
const GlobalReportsDepartmentUtilizationContainer = lazy(
  () => import('./reports/containers/time-tracking-tab/DepartmentUtilizationContainer')
)
const GlobalReportsProjectHoursPhasingContainer = lazy(
  () => import('./reports/containers/time-tracking-tab/ProjectHoursPhasingContainer')
)
const GlobalReportsExpectedPaymentsContainer = lazy(
  () => import('./reports/containers/accounting-tab/ExpectedPaymentsContainer')
)
const GlobalReportsExpectedReceiptsContainer = lazy(
  () => import('./reports/containers/accounting-tab/ExpectedReceiptsContainer')
)

// Budget Module Components
const StudyBudgetingContainer = lazy(() => import('./budget/containers/StudyBudgetingWithErrorBoundary'))
const BudgetSettingsContainer = lazy(() => import('./budget/containers/BudgetSettingsContainer'))
const BudgetDetailContainer = lazy(() => import('./budget/containers/BudgetDetailWithErrorBoundary'))
const BudgetTransferPage = lazy(() => import('./budget/project-import/BudgetTransferPage'))

// Development Components
const InternalTabStandalone = lazy(() => import('./budget/components/InternalTabStandalone'))
const TestErrorBoundaryPage = lazy(() => import('./budget/pages/TestErrorBoundaryPage'))

interface Props {
  user: IUserDetail | null
  loggedIn: boolean
  haveRight: boolean
  checkLoader: boolean
  projectAdminRight: string[]
}

const globalDashboardRoutes: IRoute[] = [
  { exact: true, path: '/dashboard', component: GlobalDashboardContainer, rights: [27] },
]

const userDashboardRoutes: IRoute[] = [
  { exact: true, path: '/user-dashboard', component: UserDashboardContainer, rights: [] },
]

const projectRoutes: IRoute[] = [
  { exact: true, path: '/projects', component: ProjectListContainer, rights: [6] },
  { exact: true, path: '/projects/:id([0-9]+)/activities', component: ProjectActivitiesContainer, rights: [6] },
  { exact: true, path: '/projects/:id([0-9]+)/dashboard', component: ProjectDashboardContainer, rights: [6] },
  { exact: false, path: '/projects/:id([0-9]+)/insights', component: ProjectInsightsContainer, rights: [21] },
  { exact: true, path: '/projects/:id([0-9]+)/budget', component: ProjectBudgetContainer, rights: [16] },
  { exact: true, path: '/projects/:id([0-9]+)/milestones', component: ProjectMilestonesContainer, rights: [28] },
  { exact: true, path: '/projects/:id([0-9]+)/expenditures', component: ProjectBudgetContainer, rights: [16] },
  { exact: true, path: '/projects/:id([0-9]+)/accounting', component: ProjectAccountingContainer, rights: [19] },
  { exact: true, path: '/projects/:id([0-9]+)/accounting/payment-list', component: PaymentListContainer, rights: [19] },
  { exact: true, path: '/projects/:id([0-9]+)/accounting/invoice-list', component: InvoiceListContainer, rights: [19] },
  { exact: false, path: '/projects/:id([0-9]+)/settings', component: ProjectSettingsContainer, rights: [14] },
  { exact: true, path: '/projects/:id([0-9]+)/budget/project-offer', component: ProjectOfferContainer, rights: [] },
]

const adminRoutes: IRoute[] = [
  { exact: true, path: '/admin', component: UserContainer, rights: [2, 3, 4] },
  { exact: true, path: '/admin/general', component: GeneralContainer, rights: [1] },
  { exact: true, path: '/admin/user', component: UserContainer, rights: [2, 3, 4] },
  { exact: true, path: '/admin/rates', component: RatesContainer, rights: [5] },
  { exact: true, path: '/profile', component: UserProfileContainer, rights: [] },
]

const settingsRoutes: IRoute[] = [...adminRoutes]

const timeTrackingRoutes: IRoute[] = [
  { exact: true, path: '/time-tracking', component: TimeTrackingContainer, rights: [22] },
  { exact: true, path: '/time-tracking/involved-tasks', component: TimeTrackingInvolvedTasksContainer, rights: [22] },
]

const accountingRoutes: IRoute[] = [
  { exact: true, path: '/accounting', component: AccountingContainer, rights: [25, 26] },
  { exact: true, path: '/accounting/:id([0-9]+)', component: ProjectAccountingContainer, rights: [25, 26] },
]

const reportsRoutes: IRoute[] = [
  { exact: true, path: '/reports', component: GlobalReportsContainer, rights: [24] },
  { exact: true, path: '/reports/tasks-by-project', component: GlobalReportsTasksByProjectContainer, rights: [24] },
  { exact: true, path: '/reports/project-list', component: GlobalReportsProjectListContainer, rights: [24] },
  { exact: true, path: '/reports/fte-by-project', component: GlobalReportsFTEByProjectContainer, rights: [24] },
  { exact: true, path: '/reports/expenditures-phasing', component: GlobalReportsExpenditurePhasingContainer, rights: [24] },
  { exact: true, path: '/reports/department-utilization', component: GlobalReportsDepartmentUtilizationContainer, rights: [24] },
  { exact: true, path: '/reports/project-hours-phasing', component: GlobalReportsProjectHoursPhasingContainer, rights: [24] },
  { exact: true, path: '/reports/expected-payments', component: GlobalReportsExpectedPaymentsContainer, rights: [24] },
  { exact: true, path: '/reports/expected-receipts', component: GlobalReportsExpectedReceiptsContainer, rights: [24] },
]

const budgetRoutes: IRoute[] = [
  { exact: true, path: '/budget', component: StudyBudgetingContainer, rights: [29] },
  { exact: true, path: '/budget/transfer', component: BudgetTransferPage, rights: [29] },
  { exact: true, path: '/budget/:id([0-9]+)/detail', component: BudgetDetailContainer, rights: [29] },
  { exact: true, path: '/budget/settings', component: BudgetSettingsContainer, rights: [29] },
  { exact: true, path: '/budget/settings/internal', component: BudgetSettingsContainer, rights: [29] },
  { exact: true, path: '/budget/settings/external', component: BudgetSettingsContainer, rights: [29] },
  { exact: true, path: '/budget/settings/units', component: BudgetSettingsContainer, rights: [29] },
  { exact: true, path: '/budget/settings/templates', component: BudgetSettingsContainer, rights: [29] },
  { exact: true, path: '/budget/settings/clients', component: BudgetSettingsContainer, rights: [29] },
  { exact: true, path: '/budget/settings/supplier', component: BudgetSettingsContainer, rights: [29] },
  { exact: true, path: '/budget/settings/categories', component: BudgetSettingsContainer, rights: [29] },
]

const developmentRoutes: IRoute[] = [
  { exact: true, path: '/dev/internal-tab', component: InternalTabStandalone, rights: [] },
  { exact: true, path: '/dev/error-boundary-test', component: TestErrorBoundaryPage, rights: [] },
]

const routes: IRoute[] = [
  ...developmentRoutes,
  ...budgetRoutes,
  ...globalDashboardRoutes,
  ...userDashboardRoutes,
  ...projectRoutes,
  ...settingsRoutes,
  ...timeTrackingRoutes,
  ...accountingRoutes,
  ...reportsRoutes,
]

/**
 * Routes for regular (non-restricted) mode.
 * Full application access with permission-based routing.
 * Note: AppRouter decides whether to render this or RestrictedModeRoutes.
 */
const ProtectedRoutes = (props: Props): JSX.Element => {
  const { user, loggedIn, haveRight, checkLoader, projectAdminRight } = props
  const [admin, setAdmin] = useState(false)
  const [projectRight, setProjectRight] = useState(true)
  const [projectSubmenuRight, setProjectSubmenuRight] = useState(true)
  const _location = useLocation()

  useEffect(() => {
    const locArr = _location.pathname.split('/').filter((i) => i !== '')
    const projectView = locArr.length > 1 && locArr.includes('projects')
    const adminRole = user?.employee?.roles.filter((r) => +r.id === 1000).length !== 0
    if (projectView && adminRole) {
      setAdmin(true)
    }
  }, [_location, user])

  useEffect(() => {
    const locArr = _location.pathname.split('/').filter((i) => i !== '')
    const projectView = locArr.length > 1 && locArr.includes('projects')
    setProjectRight(projectView ? haveRight : true)
    setProjectSubmenuRight(projectView ? projectAdminRight.some(i => locArr.includes(i)) : true)
  }, [_location, haveRight, projectAdminRight])

  // Filter routes excluding those handled separately
  const filteredRoutes = routes.filter(route =>
    !route.path.startsWith('/budget') &&
    !route.path.startsWith('/dev') &&
    !route.path.startsWith('/admin') &&
    route.path !== '/profile'
  )

  return (
    <Suspense
      fallback={
        <Dimmer active inverted>
          <Loader size='big' />
        </Dimmer>
      }
    >
      <Switch>
        {/* Budget Routes */}
        {budgetRoutes.map((route, idx) => (
          <Route key={`budget-${idx}`} exact={route.exact} path={route.path} component={route.component} />
        ))}

        {/* Development Routes */}
        {developmentRoutes.map((route, idx) => (
          <Route key={`dev-${idx}`} exact={route.exact} path={route.path} component={route.component} />
        ))}

        {/* Admin and Profile Routes */}
        {settingsRoutes.map((route, idx) => (
          <Route key={`settings-${idx}`} exact={route.exact} path={route.path} component={route.component} />
        ))}

        {/* Main Routes with Permission Checks */}
        {filteredRoutes.map((route, idx) => {
          if (route.component) {
            const hasPermission = (user && user.id !== '' || loggedIn) && projectRight && projectSubmenuRight && ((user && checkRole(user, route.rights)) || admin)

            if (hasPermission) {
              return <Route key={idx} exact={route.exact} path={route.path} component={route.component} />
            }
            return (
              <Route
                key={idx}
                exact={route.exact}
                path={route.path}
                component={() => user && (user.id === '' || checkRole(user, route.rights)) && checkLoader ? <Loader size='big' /> : <></>}
              />
            )
          }
          return null
        })}

        {/* Default route - redirect based on user role */}
        <Route exact path="/">
          {user && checkRole(user, [27]) ? (
            <Redirect to="/dashboard" />
          ) : (
            <Redirect to="/user-dashboard" />
          )}
        </Route>
      </Switch>
    </Suspense>
  )
}

export default ProtectedRoutes
